import React, { useState, useEffect } from 'react';

// Get API base URL from environment
const API_BASE = process.env.REACT_APP_API_BASE || '';

const PublicInvoice = ({ encodedOrderId }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        
        // Call the public API endpoint
        const response = await fetch(`${API_BASE}/public/invoice/${encodedOrderId}`);
        const data = await response.json();
        
        if (data.success) {
          setInvoice(data.data);
          // Update meta tags for WhatsApp preview
          if (window.updateInvoiceMetaTags) {
            window.updateInvoiceMetaTags(data.data);
          }
        } else {
          setError(data.message || 'Invoice not found');
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
        setError('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    if (encodedOrderId) {
      fetchInvoice();
    }
  }, [encodedOrderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invoice Not Found</h1>
          <p className="text-gray-600">{error || 'The requested invoice could not be found.'}</p>
        </div>
      </div>
    );
  }

  // Calculate totals and discounts
  const total = invoice.total || invoice.sellingPrice || 0;
  
  // Calculate subtotal from items (sum of base prices before discounts)
  const calculatedSubtotal = invoice.items?.reduce((sum, item) => {
    const baseTotal = (item.price || 0) * (item.quantity || 0);
    return sum + baseTotal;
  }, 0) || 0;
  
  const subtotal = invoice.subTotal || calculatedSubtotal || total;
  
  // Calculate item-level discounts
  const itemDiscountTotal = invoice.items?.reduce((sum, item) => {
    const baseTotal = (item.price || 0) * (item.quantity || 0);
    const itemDiscount = item.discountPercent ? (baseTotal * item.discountPercent / 100) : 0;
    return sum + itemDiscount;
  }, 0) || 0;
  
  // Get overall discount - check multiple sources
  let discount = invoice.discount || 0;
  
  // If discount is not provided but we have subtotal and total, calculate it
  if (discount === 0 && subtotal > total && subtotal > 0) {
    discount = subtotal - total;
  }
  
  // Calculate overall discount (total discount minus item discounts)
  const overallDiscount = Math.max(0, discount - itemDiscountTotal);
  
  // Total discount is sum of item discounts and overall discount
  const totalDiscount = itemDiscountTotal + overallDiscount;
  
  // Has discount if there's any discount (item-level or overall)
  const hasDiscount = totalDiscount > 0;
  
  // Calculate item totals with discounts
  const getItemTotal = (item) => {
    const baseTotal = (item.price || 0) * (item.quantity || 0);
    const itemDiscount = item.discountPercent ? (baseTotal * item.discountPercent / 100) : 0;
    return {
      baseTotal,
      discount: itemDiscount,
      finalTotal: baseTotal - itemDiscount,
      hasDiscount: itemDiscount > 0
    };
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Green top border */}
      <div className="h-1 bg-green-600"></div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Business Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">
                {invoice?.businessSettings?.name || 'Your Business'}
              </h1>
              {invoice?.businessSettings?.address && (
                <p className="text-gray-700 text-sm sm:text-base mb-1">{invoice.businessSettings.address}</p>
              )}
              {invoice?.businessSettings?.phone && (
                <p className="text-gray-700 text-sm sm:text-base mb-1">Phone: {invoice.businessSettings.phone}</p>
              )}
              {invoice?.businessSettings?.email && (
                <p className="text-gray-700 text-sm sm:text-base">Email: {invoice.businessSettings.email}</p>
              )}
            </div>
            
            {/* Logo */}
            {invoice?.businessSettings?.logoDataUrl && (
              <div className="ml-4 flex-shrink-0">
                <img
                  src={invoice.businessSettings.logoDataUrl}
                  alt="Business Logo"
                  className="h-16 w-16 sm:h-20 sm:w-20 object-contain rounded-lg"
                />
              </div>
            )}
          </div>
        </div>

        {/* Invoice Title */}
        <div className="bg-green-600 text-white px-4 py-3 rounded-lg mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg sm:text-xl font-bold">INVOICE: {invoice.name}</h2>
            <p className="text-sm sm:text-base mt-1 sm:mt-0">
              Date: {new Date(invoice.date).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Customer Details */}
        <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-6">
          <h3 className="text-lg font-bold text-green-600 mb-3">Bill To:</h3>
          <div className="text-gray-800">
            <p className="font-medium text-base sm:text-lg">{invoice.customerName}</p>
            {invoice.customerPhone && (
              <p className="text-sm sm:text-base mt-1">Phone: {invoice.customerPhone}</p>
            )}
            {invoice.customerAddress && (
              <p className="text-sm sm:text-base mt-1">Address: {invoice.customerAddress}</p>
            )}
            {invoice.customerEmail && (
              <p className="text-sm sm:text-base mt-1">Email: {invoice.customerEmail}</p>
            )}
            {invoice.customerNumber && (
              <p className="text-sm sm:text-base mt-1">Customer #: {invoice.customerNumber}</p>
            )}
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
          {/* Table Header */}
          <div className="bg-green-600 text-white px-4 py-3">
            <div className="grid grid-cols-4 gap-4 text-sm font-bold">
              <div className="text-left">Item Description</div>
              <div className="text-center">Qty</div>
              <div className="text-right">Unit Price</div>
              <div className="text-right">Total</div>
            </div>
          </div>

          {/* Table Body - Scrollable */}
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {invoice.items?.map((item, index) => {
              const itemTotals = getItemTotal(item);
              return (
                <div
                  key={index}
                  className={`px-4 ${itemTotals.hasDiscount ? 'py-4' : 'py-3'} grid grid-cols-4 gap-4 text-sm ${
                    index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                  }`}
                >
                  <div className="text-gray-800 font-medium">{item.name}</div>
                  <div className="text-center text-gray-600">{item.quantity}</div>
                  <div className="text-right text-gray-600">₹{item.price.toFixed(2)}</div>
                  <div className="text-right">
                    {itemTotals.hasDiscount ? (
                      <div className="space-y-1">
                        <div className="text-gray-400 text-xs line-through">
                          ₹{itemTotals.baseTotal.toFixed(2)}
                        </div>
                        <div className="text-green-600 font-bold text-xs">
                          -₹{itemTotals.discount.toFixed(2)} ({item.discountPercent}%)
                        </div>
                        <div className="text-gray-800 font-bold">
                          ₹{itemTotals.finalTotal.toFixed(2)}
                        </div>
                      </div>
                    ) : (
                      <div className="font-medium text-gray-800">
                        ₹{itemTotals.baseTotal.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              );
            }) || (
              <div className="px-4 py-3 grid grid-cols-4 gap-4 text-sm bg-gray-50">
                <div className="text-gray-800 font-medium">Service</div>
                <div className="text-center text-gray-600">1</div>
                <div className="text-right text-gray-600">₹{total.toFixed(2)}</div>
                <div className="text-right font-medium text-gray-800">₹{total.toFixed(2)}</div>
              </div>
            )}
          </div>
        </div>

        {/* Total Section */}
        <div className={`bg-green-50 border-l-4 border-green-600 rounded-r-lg p-4 sm:p-6 mb-8 ${hasDiscount ? 'space-y-3' : ''}`}>
          {hasDiscount ? (
            <>
              <div className="flex justify-between items-center text-sm text-gray-700">
                <span>Subtotal:</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              
              {itemDiscountTotal > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-green-600">Item Discounts:</span>
                  <span className="text-sm font-bold text-green-600">-₹{itemDiscountTotal.toFixed(2)}</span>
                </div>
              )}
              
              {overallDiscount > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-base font-bold text-green-600">Overall Discount:</span>
                  <span className="text-base font-bold text-green-600">-₹{overallDiscount.toFixed(2)}</span>
                </div>
              )}
              
              {totalDiscount > 0 && (
                <div className="bg-green-100 border border-green-300 rounded-lg p-3 mt-3 mb-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-700">
                      🎉 You Saved ₹{totalDiscount.toFixed(2)}! 🎉
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-center pt-3 border-t-2 border-green-300">
                <span className="text-lg font-bold text-green-600">Total Amount:</span>
                <span className="text-2xl font-bold text-green-600">₹{total.toFixed(2)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-green-600">Total Amount:</span>
              <span className="text-2xl font-bold text-green-600">₹{total.toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mb-8">
          <p className="text-lg font-bold text-green-600 mb-2">
            {invoice?.businessSettings?.footerText || 'Thank you for your business!'}
          </p>
          <p className="text-sm text-gray-500">
            Invoice generated on: {new Date(invoice.createdDate).toLocaleDateString()} at {new Date(invoice.createdDate).toLocaleTimeString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">This is a computer-generated invoice.</p>
        </div>

        {/* App Promotion */}
        <div className="text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold text-blue-800 mb-2">Want to track your business profit?</h3>
            <p className="text-blue-600 mb-4">Get started with TrackProfit - the complete business management solution</p>
            <a
              href="https://wa.me/919543268625?text=Hi! I want to track my business profit"
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
              Send Hi to WhatsApp +919543268625
            </a>
          </div>
        </div>

        {/* Bottom green border */}
        <div className="h-1 bg-green-600 mt-8"></div>
      </div>
    </div>
  );
};

export default PublicInvoice;